import { useEffect, useMemo, useState } from "react";
import {
  CORE_CHARACTERS,
  createFallback,
  type CoreCharacter,
  type ExpressionTopic,
} from "../shared/refinement";
import { refineExpression } from "./api";
import { FIRST_CHOICES, FIRST_REFLECTIONS, POEM, SCREEN_TITLES, SOURCES, TOPICS } from "./content";
import { ArtifactPlacement } from "./components/ArtifactPlacement";
import { BambooArt } from "./components/BambooArt";
import { LeafPuzzle } from "./components/LeafPuzzle";
import { NameBambooArt } from "./components/NameBambooArt";
import { NarrativeSeal } from "./components/NarrativeSeal";
import { PoemBambooArt } from "./components/PoemBambooArt";
import { createNameShareCard, createShareCard } from "./share";
import { playInkSound } from "./sound";
import { INITIAL_STATE, type ArtView, type BambooStyle, type GameState } from "./types";

const STORAGE_KEY = "fengyu-cangxin-session-v1";

function loadState(): GameState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? { ...INITIAL_STATE, ...(JSON.parse(raw) as Partial<GameState>) } : INITIAL_STATE;
  } catch {
    return INITIAL_STATE;
  }
}

function InkDivider() {
  return <span className="ink-divider" aria-hidden="true" />;
}

function App() {
  const [game, setGame] = useState<GameState>(loadState);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [leaveSlider, setLeaveSlider] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [nameDraft, setNameDraft] = useState(game.hiddenName);
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(game));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [game]);

  const update = (patch: Partial<GameState>) => setGame((current) => ({ ...current, ...patch }));
  const next = () => {
    playInkSound(soundEnabled, "leaf");
    update({ screen: Math.min(14, game.screen + 1) });
  };
  const back = () => update({ screen: Math.max(0, game.screen - 1) });
  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  };

  const firstReflection = useMemo(() => {
    if (!game.firstChoice) return null;
    if (game.firstChoice === "自己输入一句") {
      return {
        mark: "心",
        text: `你把“${game.firstCustom.trim() || "尚未说完的话"}”放在了更前面。这片叶，只记此刻。`,
      };
    }
    return FIRST_REFLECTIONS[game.firstChoice];
  }, [game.firstChoice, game.firstCustom]);

  const submitExpression = async () => {
    if (!game.topic || game.originalInput.trim().length < 2) {
      setError("请先写下至少两个字。");
      return;
    }
    setError("");
    setLoading(true);
    update({ screen: 11, refinement: null });
    try {
      const result = await refineExpression(game.topic, game.originalInput.trim(), game.privacyMode);
      update({
        refinement: result,
        selectedPhrase: result.phrases[0],
        coreCharacter: result.core_character,
        bambooStyle: result.recommended_bamboo,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "风声未至，请稍后再试。");
      update({ screen: 10 });
    } finally {
      setLoading(false);
    }
  };

  const changePhrase = (index: number, value: string) => {
    if (!game.refinement) return;
    const phrases = [...game.refinement.phrases] as [string, string, string];
    phrases[index] = value.slice(0, 16);
    update({ refinement: { ...game.refinement, phrases } });
  };

  const saveOrShare = async (share: boolean) => {
    if (!game.refinement) return;
    try {
      const blob = await createShareCard(
        game.coreCharacter,
        game.selectedPhrase,
        game.refinement.reflection,
        game.bambooStyle,
      );
      const file = new File([blob], "我的风雨竹.png", { type: "image/png" });
      if (share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: "我的风雨竹",
          text: `我的竹里藏着一个“${game.coreCharacter}”字。你会把什么藏进风雨？`,
          files: [file],
        });
        notify("分享面板已经打开。原话没有进入分享图。");
        return;
      }
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "我的风雨竹.png";
      anchor.click();
      URL.revokeObjectURL(url);
      notify(share ? "当前浏览器不支持文件分享，已改为保存图片。" : "风雨竹已经保存。原话没有进入图片。");
    } catch {
      notify("图片未能生成，请截图保存当前作品。");
    }
  };

  const generateNameArt = () => {
    const name = nameDraft.trim();
    if (!/^[\u3400-\u9fff]{1,4}$/u.test(name)) {
      setNameError("请输入 1—4 个汉字；建议使用昵称，不要填写账号或联系方式。");
      return;
    }
    setNameError("");
    update({ hiddenName: name, nameArtView: "character" });
    notify("名字已经藏进竹叶。它没有发送到服务器。");
  };

  const saveNameArt = async () => {
    if (!game.hiddenName) return;
    try {
      const blob = await createNameShareCard(game.hiddenName, game.bambooStyle);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "我的藏名竹画.png";
      anchor.click();
      URL.revokeObjectURL(url);
      notify("藏名竹画已经保存。名字只存在于本地图片中。");
    } catch {
      notify("藏名图未能生成，请截图保存当前作品。");
    }
  };

  const restart = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setGame(INITIAL_STATE);
    setLeaveSlider(0);
    setError("");
    setNameDraft("");
    setNameError("");
  };

  const screen = (() => {
    switch (game.screen) {
      case 0:
        return (
          <section className="scene scene--hero" aria-labelledby="hero-title">
            <div className="hero-wash" aria-hidden="true" />
            <div className="hero-copy">
              <p className="eyebrow">关帝诗竹 · 游戏演绎</p>
              <h1 id="hero-title">风雨藏心</h1>
              <p className="subtitle">一幅竹画，藏着二十个字；一段旧事，问你如何不负本心。</p>
              <InkDivider />
              <p className="opening-lines">远看是竹，近看是字。<br />史书写下他的选择，后人把选择画进风雨。</p>
              <button type="button" className="button button--primary button--large" onClick={next}>
                入竹辨心
              </button>
              <p className="time-note">约 8—12 分钟 · 无需登录 · 可静音完成</p>
            </div>
          </section>
        );
      case 1:
        return (
          <section className="scene" aria-labelledby="layers-title">
            <p className="eyebrow">入碑之前</p>
            <h1 id="layers-title">先认三枚墨印</h1>
            <p className="lead">本作以史书记载为骨架，并结合《关帝诗竹》的民间传说进行游戏演绎。</p>
            <div className="seal-grid">
              <NarrativeSeal layer="history" />
              <NarrativeSeal layer="legend" />
              <NarrativeSeal layer="game" />
            </div>
            <p className="quiet-panel">墨印会出现在叙事角落。点开它，便知道眼前所见来自哪里。</p>
            <button type="button" className="button button--primary" onClick={next}>我记住了</button>
          </section>
        );
      case 2:
        return (
          <section className="scene" aria-labelledby="choice-title">
            <NarrativeSeal layer="history" />
            <p className="chapter">第一章</p>
            <h1 id="choice-title">新恩与旧约</h1>
            <p className="story-text">下邳失守后，关羽为曹军所得。曹操礼待甚厚，旧日的约定却并未因此消失。</p>
            <blockquote>一个在你落难时厚待你的人，希望你留下；而你早已对另一个人许下承诺。此刻你最不能放下的是什么？</blockquote>
            <div className="choice-list">
              {FIRST_CHOICES.map((choice) => (
                <button
                  type="button"
                  key={choice}
                  className={`choice-card ${game.firstChoice === choice ? "is-selected" : ""}`}
                  aria-pressed={game.firstChoice === choice}
                  onClick={() => update({ firstChoice: choice })}
                >
                  {choice}
                </button>
              ))}
            </div>
            {game.firstChoice === "自己输入一句" && (
              <label className="field-label">
                你的答案
                <input
                  value={game.firstCustom}
                  maxLength={36}
                  onChange={(event) => update({ firstCustom: event.target.value })}
                  placeholder="不必写真实姓名"
                />
              </label>
            )}
            {firstReflection && (game.firstChoice !== "自己输入一句" || game.firstCustom.trim()) && (
              <div className="reflection-card" aria-live="polite">
                <span className="leaf-mark">{firstReflection.mark}</span>
                <p>{firstReflection.text}</p>
              </div>
            )}
            <button
              type="button"
              className="button button--primary"
              disabled={!game.firstChoice || (game.firstChoice === "自己输入一句" && !game.firstCustom.trim())}
              onClick={next}
            >
              看史书如何记
            </button>
          </section>
        );
      case 3:
        return (
          <section className="scene scene--history" aria-labelledby="history-title">
            <NarrativeSeal layer="history" />
            <p className="eyebrow">史书记</p>
            <h1 id="history-title">难的不是忘恩</h1>
            <p className="story-text story-text--large">曹操待他很厚。关羽却明白表示：自己受刘备厚恩，誓以共死，不可背弃。</p>
            <p className="ink-quote">他没有把新恩当作不存在，也没有让新恩改写旧约。</p>
            <button type="button" className="button button--primary" onClick={next}>继续听风</button>
          </section>
        );
      case 4:
        return (
          <section className="scene" aria-labelledby="leave-title">
            <NarrativeSeal layer="history" />
            <p className="chapter">第二章</p>
            <h1 id="leave-title">先走，还是先报恩</h1>
            <p className="story-text">此刻离开最安全；留下，则可能再也走不了。</p>
            <div className="decision-scale">
              <span>立即离开</span><span>先报一事</span>
              <input
                aria-label="拖动去字叶片决定立即离开或先报一事"
                type="range"
                min="-100"
                max="100"
                value={leaveSlider}
                onChange={(event) => setLeaveSlider(Number(event.target.value))}
              />
              <div className="moving-leaf" style={{ left: `${50 + leaveSlider * 0.36}%` }}>去</div>
            </div>
            {Math.abs(leaveSlider) >= 55 && (
              <div className="reflection-card" aria-live="polite">
                <span className="leaf-mark">{leaveSlider < 0 ? "去" : "恩"}</span>
                <p>{leaveSlider < 0 ? "你选择先离开危险，再想怎样回应这份厚待。" : "你选择先完成一件事，再把自己的去向说清楚。"}</p>
              </div>
            )}
            <button
              type="button"
              className="button button--primary"
              disabled={Math.abs(leaveSlider) < 55}
              onClick={() => {
                update({ leaveDecision: leaveSlider < 0 ? "leave" : "repay" });
                next();
              }}
            >
              落定此念
            </button>
          </section>
        );
      case 5:
        return (
          <section className="scene" aria-labelledby="seal-gold-title">
            <NarrativeSeal layer="history" />
            <p className="chapter">第三章</p>
            <h1 id="seal-gold-title">封金挂印</h1>
            <p className="story-text">立功之后，重赏仍在案前。请把三件东西放回它们应在的位置。</p>
            <ArtifactPlacement placed={game.artifactsPlaced} onPlaced={(ids) => update({ artifactsPlaced: ids })} />
            {game.artifactsPlaced.length === 3 && (
              <div className="reflection-card reflection-card--center" aria-live="polite">
                <p>带走的是选择，留下的是厚意。</p>
              </div>
            )}
            <button type="button" className="button button--primary" disabled={game.artifactsPlaced.length !== 3} onClick={next}>
              看竹如何生长
            </button>
          </section>
        );
      case 6:
        return (
          <section className="scene scene--art" aria-labelledby="storm-title">
            <NarrativeSeal layer="legend" />
            <h1 id="storm-title">风雨成字</h1>
            <p className="lead">一枝受风，叶片斜飞；一枝承雨，叶片下垂。后世把这段心志藏进二十个字，又把二十个字画成两枝竹。</p>
            <div className="double-bamboo">
              <BambooArt character="义" style="wind" labelled={false} />
              <BambooArt character="信" style="rain" labelled={false} />
            </div>
            <button type="button" className="button button--primary" onClick={next}>拆叶辨字</button>
          </section>
        );
      case 7:
        return (
          <section className="scene" aria-labelledby="puzzle-title">
            <NarrativeSeal layer="game" />
            <h1 id="puzzle-title">叶片组字</h1>
            <p className="lead">一叶是笔，个叶成组，介叶分枝。你不是写字，而是让竹叶重新认出自己的骨架。</p>
            <LeafPuzzle
              initialStage={game.puzzleStage}
              onProgress={(stage) => update({ puzzleStage: stage })}
              onComplete={next}
            />
          </section>
        );
      case 8:
        return (
          <section className="scene scene--poem" aria-labelledby="poem-title">
            <NarrativeSeal layer="legend" />
            <h1 id="poem-title">藏诗揭晓</h1>
            <div className="view-tabs" role="tablist" aria-label="藏诗观看状态">
              {(["painting", "character", "meaning"] as ArtView[]).map((view) => (
                <button
                  type="button"
                  role="tab"
                  key={view}
                  aria-selected={game.artView === view}
                  className={game.artView === view ? "is-active" : ""}
                  onClick={() => update({ artView: view })}
                >
                  {view === "painting" ? "看画" : view === "character" ? "看字" : "看意"}
                </button>
              ))}
            </div>
            <div className={`poem-art poem-art--${game.artView}`}>
              <PoemBambooArt view={game.artView} />
              {game.artView === "character" && <p className="character-guide">四列叶群由右向左，各藏五字</p>}
            </div>
            {game.artView !== "painting" && (
              <div className={`poem-copy poem-copy--${game.artView}`} aria-label="关帝诗竹二十字">
                {POEM.map((line, index) => <p key={line}><span>0{index + 1}</span>{line}</p>)}
              </div>
            )}
            {game.artView === "meaning" && (
              <p className="quiet-panel">在传说语境中，它被解释为：不因新恩与富贵改变旧志；即使一时孤弱，也守住不会凋零的本心。</p>
            )}
            <button type="button" className="button button--primary" onClick={next}>回到自己的风雨</button>
          </section>
        );
      case 9:
        return (
          <section className="scene" aria-labelledby="self-title">
            <NarrativeSeal layer="game" />
            <h1 id="self-title">你不必成为关羽</h1>
            <blockquote>只需想一想：此刻的风雨里，你最想守住哪一个字？</blockquote>
            <div className="topic-list">
              {TOPICS.map((topic) => (
                <button
                  type="button"
                  key={topic.value}
                  className={`topic-card ${game.topic === topic.value ? "is-selected" : ""}`}
                  aria-pressed={game.topic === topic.value}
                  onClick={() => update({ topic: topic.value })}
                >
                  <strong>{topic.label}</strong>
                  <span>{topic.hint}</span>
                </button>
              ))}
            </div>
            <button type="button" className="button button--primary" disabled={!game.topic} onClick={next}>写下一句</button>
          </section>
        );
      case 10:
        return (
          <section className="scene" aria-labelledby="input-title">
            <NarrativeSeal layer="game" />
            <p className="eyebrow">{game.topic}</p>
            <h1 id="input-title">把话留在纸上</h1>
            <p className="lead">不需要古雅，也不需要完整。写你真正想说的那一句。</p>
            <label className="field-label field-label--large">
              你的原话
              <textarea
                value={game.originalInput}
                maxLength={120}
                rows={6}
                onChange={(event) => update({ originalInput: event.target.value })}
                placeholder="例如：我想开始新的路，但也想好好告别现在的伙伴。"
              />
              <span>{game.originalInput.length}/120</span>
            </label>
            <div className="privacy-note">
              <strong>请不要输入真实姓名、账号、电话或地址。</strong>
              <span>原话不进入分享图，也不在服务端保存。</span>
            </div>
            <label className="privacy-toggle">
              <input
                type="checkbox"
                checked={game.privacyMode}
                onChange={(event) => update({ privacyMode: event.target.checked })}
              />
              <span><strong>只用模板，不发送原句给 AI</strong><small>仍可完成全部体验</small></span>
            </label>
            {error && <p className="error-message" role="alert">{error}</p>}
            <button type="button" className="button button--primary" disabled={game.originalInput.trim().length < 2} onClick={() => void submitExpression()}>
              借竹明志
            </button>
          </section>
        );
      case 11:
        return (
          <section className="scene" aria-labelledby="refine-title">
            <NarrativeSeal layer="game" />
            <h1 id="refine-title">墨竹只替你磨一句</h1>
            {loading || !game.refinement ? (
              <div className="ink-loading" role="status">
                <span /><span /><span />
                <p>墨色正在纸上散开……</p>
              </div>
            ) : (
              <>
                <p className="reflection-line">{game.refinement.reflection}</p>
                {game.refinement.source !== "ai" && (
                  <p className="fallback-notice">
                    {game.refinement.source === "safety"
                      ? "这次不生成诗意判断，只先把安全放在前面。"
                      : "这次由编辑过的句库完成，流程不因模型缺席而中断。"}
                  </p>
                )}
                <fieldset className="phrase-list">
                  <legend>选择一句，也可以改一两个字</legend>
                  {game.refinement.phrases.map((phrase, index) => (
                    <label key={index} className={`phrase-option ${game.selectedPhrase === phrase ? "is-selected" : ""}`}>
                      <input
                        type="radio"
                        name="phrase"
                        checked={game.selectedPhrase === phrase}
                        onChange={() => update({ selectedPhrase: phrase })}
                      />
                      <textarea
                        aria-label={`心句版本 ${index + 1}`}
                        value={phrase}
                        rows={1}
                        maxLength={16}
                        onFocus={() => update({ selectedPhrase: phrase })}
                        onChange={(event) => {
                          changePhrase(index, event.target.value);
                          update({ selectedPhrase: event.target.value });
                        }}
                      />
                    </label>
                  ))}
                </fieldset>
                <div className="character-picker">
                  <p>核心字</p>
                  <div>
                    {CORE_CHARACTERS.map((character) => (
                      <button
                        type="button"
                        key={character}
                        className={game.coreCharacter === character ? "is-selected" : ""}
                        aria-pressed={game.coreCharacter === character}
                        onClick={() => update({ coreCharacter: character })}
                      >
                        {character}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="button" className="button button--primary" disabled={!game.selectedPhrase.trim()} onClick={next}>
                  我确认这句话
                </button>
              </>
            )}
          </section>
        );
      case 12:
        return (
          <section className="scene scene--studio" aria-labelledby="studio-title">
            <NarrativeSeal layer="game" />
            <h1 id="studio-title">竹画工坊</h1>
            <p className="lead">构图改变风势与留白，不改变你确认的字。</p>
            <BambooArt
              character={game.coreCharacter}
              phrase={game.selectedPhrase}
              view="character"
              style={game.bambooStyle}
              className="studio-art"
            />
            <div className="style-picker" role="group" aria-label="选择竹画构图">
              {(["wind", "rain", "sparse", "dense"] as BambooStyle[]).map((style) => (
                <button
                  type="button"
                  key={style}
                  aria-pressed={game.bambooStyle === style}
                  className={game.bambooStyle === style ? "is-selected" : ""}
                  onClick={() => update({ bambooStyle: style })}
                >
                  <strong>{style === "wind" ? "风竹" : style === "rain" ? "雨竹" : style === "sparse" ? "疏竹" : "密竹"}</strong>
                  <span>{style === "wind" ? "弯而不断" : style === "rain" ? "垂叶承意" : style === "sparse" ? "淡墨留白" : "收束守心"}</span>
                </button>
              ))}
            </div>
            <button type="button" className="button button--primary" onClick={next}>落墨成画</button>
          </section>
        );
      case 13:
        return (
          <section className="scene scene--final" aria-labelledby="final-title">
            <p className="eyebrow">我的风雨竹</p>
            <h1 id="final-title" className="visually-hidden">我的风雨竹</h1>
            <div className="final-card">
              <BambooArt
                character={game.coreCharacter}
                phrase={game.selectedPhrase}
                view={game.artView}
                style={game.bambooStyle}
              />
              {game.artView === "meaning" && game.refinement && (
                <div className="final-meaning">
                  <strong>{game.selectedPhrase}</strong>
                  <p>{game.refinement.reflection}</p>
                </div>
              )}
            </div>
            <div className="view-tabs" role="tablist" aria-label="作品观看状态">
              {(["painting", "character", "meaning"] as ArtView[]).map((view) => (
                <button
                  type="button"
                  role="tab"
                  key={view}
                  aria-selected={game.artView === view}
                  className={game.artView === view ? "is-active" : ""}
                  onClick={() => update({ artView: view })}
                >
                  {view === "painting" ? "看画" : view === "character" ? "看字" : "看意"}
                </button>
              ))}
            </div>
            <p className="share-line">我的竹里藏着一个“{game.coreCharacter}”字。<br />你会把什么藏进风雨？</p>
            <div className="button-row">
              <button type="button" className="button button--quiet" onClick={() => void saveOrShare(false)}>保存长图</button>
              <button type="button" className="button button--primary" onClick={() => void saveOrShare(true)}>分享作品</button>
            </div>
            <p className="microcopy">分享图只含确认后的心句，不含你的原话。</p>
            <section className="name-workshop" aria-labelledby="name-workshop-title">
              <p className="eyebrow">新增玩法 · 一叶藏一名</p>
              <h2 id="name-workshop-title">把名字藏进竹叶</h2>
              <p>输入一到四个汉字，生成一幅只属于本地的藏名竹画。建议使用昵称。</p>
              <div className="name-input-row">
                <label>
                  <span className="visually-hidden">一到四字昵称</span>
                  <input
                    value={nameDraft}
                    maxLength={4}
                    inputMode="text"
                    autoComplete="off"
                    placeholder="例如：长风"
                    onChange={(event) => setNameDraft(event.target.value)}
                  />
                </label>
                <button type="button" className="button button--ink" onClick={generateNameArt}>生成藏名竹画</button>
              </div>
              <p className="microcopy">名字不发送 AI、不写入服务端；点击生成表示你确认它会出现在这张本地作品中。</p>
              {nameError && <p className="error-message" role="alert">{nameError}</p>}
              {game.hiddenName && (
                <div className="name-art-result">
                  <NameBambooArt name={game.hiddenName} view={game.nameArtView} style={game.bambooStyle} />
                  <div className="view-tabs" role="tablist" aria-label="藏名竹画观看状态">
                    {(["painting", "character", "meaning"] as ArtView[]).map((view) => (
                      <button
                        type="button"
                        role="tab"
                        key={view}
                        aria-selected={game.nameArtView === view}
                        className={game.nameArtView === view ? "is-active" : ""}
                        onClick={() => update({ nameArtView: view })}
                      >
                        {view === "painting" ? "看画" : view === "character" ? "看名" : "看意"}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="button button--quiet" onClick={() => void saveNameArt()}>保存藏名图</button>
                </div>
              )}
            </section>
            <button type="button" className="text-button" onClick={next}>翻到碑外一页</button>
          </section>
        );
      case 14:
        return (
          <section className="scene" aria-labelledby="sources-title">
            <h1 id="sources-title">碑外一页</h1>
            <p className="lead">史实建立可信，传说留下想象。把两者分开，才能看见这幅竹为什么流传。</p>
            <div className="source-list">
              {SOURCES.map((source, index) => (
                <article key={source.title}>
                  <span>0{index + 1}</span>
                  <div>
                    <h2>
                      {"url" in source ? (
                        <a href={source.url} target="_blank" rel="noreferrer">{source.title}</a>
                      ) : source.title}
                    </h2>
                    <p>{source.text}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="three-seals" aria-label="本作叙事层级">
              <NarrativeSeal layer="history" /><NarrativeSeal layer="legend" /><NarrativeSeal layer="game" />
            </div>
            <blockquote>史书写下了他的选择，后人把他的选择画成了竹。</blockquote>
            <button type="button" className="button button--primary" onClick={restart}>再入风雨</button>
            <p className="closing-mark">#一叶藏一言</p>
          </section>
        );
      default:
        return null;
    }
  })();

  return (
    <div className="app-shell">
      {game.screen > 0 && (
        <header className="topbar">
          <button type="button" className="icon-button" onClick={back} disabled={game.screen === 0} aria-label="返回上一页">←</button>
          <div className="progress-wrap">
            <span>{SCREEN_TITLES[game.screen]}</span>
            <div className="progress-track" aria-label={`进度 ${game.screen + 1}/15`}>
              <i style={{ width: `${((game.screen + 1) / 15) * 100}%` }} />
            </div>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={() => {
              const enabled = !soundEnabled;
              setSoundEnabled(enabled);
              playInkSound(enabled, "seal");
            }}
            aria-label={soundEnabled ? "静音" : "开启声音"}
            aria-pressed={soundEnabled}
          >
            {soundEnabled ? "声" : "静"}
          </button>
        </header>
      )}
      <main>{screen}</main>
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}

export default App;
